
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { queueCoordinator } from '@/services/queue-coordinator';

interface ConnectivityState {
  isOnline: boolean;
  wasOffline: boolean;
  lastOnline: number | null;
  connectionType: 'none' | 'wifi' | 'cellular' | 'ethernet' | 'unknown';
  isSlowConnection: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ConnectivityContext = createContext<ConnectivityState>({
  isOnline: true,
  wasOffline: false,
  lastOnline: null,
  connectionType: 'unknown',
  isSlowConnection: false,
});

export function ConnectivityProvider({ children }: { children: React.ReactNode }) {
  const [connectivity, setConnectivity] = useState<ConnectivityState>({
    isOnline: navigator.onLine,
    wasOffline: false,
    lastOnline: navigator.onLine ? Date.now() : null,
    connectionType: 'unknown',
    isSlowConnection: false,
  });

  // Enhanced connectivity check
  const checkInternetConnectivity = async (): Promise<{
    isOnline: boolean;
    isSlowConnection: boolean;
    connectionType: 'none' | 'wifi' | 'cellular' | 'ethernet' | 'unknown';
  }> => {
    try {
      // First check if browser thinks we're online
      if (!navigator.onLine) {
        return {
          isOnline: false,
          isSlowConnection: false,
          connectionType: 'none'
        };
      }

      // Get connection type if available
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      let connectionType: 'none' | 'wifi' | 'cellular' | 'ethernet' | 'unknown' = 'unknown';
      let isSlowConnection = false;

      if (connection) {
        // Map effective connection types
        const effectiveType = connection.effectiveType || connection.type;

        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          isSlowConnection = true;
        }

        // Determine connection type
        if (connection.type === 'wifi') {
          connectionType = 'wifi';
        } else if (connection.type === 'cellular') {
          connectionType = 'cellular';
        } else if (connection.type === 'ethernet') {
          connectionType = 'ethernet';
        }
      }

      // Test actual internet connectivity with reduced timeout for critical path
      const startTime = Date.now();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout')), 2000); // Reduced timeout
      });

      // Connectivity test: Supabase health check (most important for our app)
      const connectivityTests = [
        supabase.from('templates').select('count').limit(1),
      ];

      const testResults = await Promise.race([
        Promise.allSettled(connectivityTests),
        timeoutPromise
      ]) as PromiseSettledResult<any>[];

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      // Check if connection is slow (over 3 seconds)
      if (responseTime > 3000) {
        isSlowConnection = true;
      }

      // At least one test must succeed
      const hasSuccessfulTest = testResults.some(result => result.status === 'fulfilled');

      return {
        isOnline: hasSuccessfulTest,
        isSlowConnection,
        connectionType
      };

    } catch (error) {
      // Only log in production to reduce console noise
      if (import.meta.env.PROD) {
        console.warn('Connectivity check failed:', error);
      }
      return {
        isOnline: false,
        isSlowConnection: false,
        connectionType: 'none'
      };
    }
  };

  // Update connectivity state
  const updateConnectivityState = async () => {
    // Optimization: Skip health checks if the tab is in the background
    if (document.visibilityState === 'hidden') {
      return;
    }

    const prevOnline = connectivity.isOnline;
    const { isOnline, isSlowConnection, connectionType } = await checkInternetConnectivity();

    setConnectivity(prev => ({
      isOnline,
      wasOffline: prev.isOnline && !isOnline,
      lastOnline: isOnline ? Date.now() : prev.lastOnline,
      connectionType,
      isSlowConnection,
    }));

    // AUTOMATIC QUEUE PROCESSING: If we just came back online, process pending recordings
    if (!prevOnline && isOnline) {
      console.log('🔄 Back online - automatically processing pending recordings...');

      // Get current user from Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        // Trigger queue processing in the background (don't await to avoid blocking)
        queueCoordinator.processQueue(session.user.id, 'connectivity_restored').catch(err => {
          console.error('Auto queue processing failed:', err);
        });
      }
    }
  };

  useEffect(() => {
    // Skip connectivity checks on localhost/development
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '';

    if (isLocalhost) {
      console.log('[ConnectivityService] Skipping connectivity checks on localhost');
      return;
    }

    // CRITICAL: Completely defer connectivity check to break critical request chain
    const deferredConnectivityCheck = () => {
      // Wait much longer to ensure critical rendering is completely done
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          // Double defer to ensure we're completely out of critical path
          setTimeout(updateConnectivityState, 1000);
        }, { timeout: 8000 });
      } else {
        // Fallback: wait 5+ seconds to be completely outside critical path
        setTimeout(updateConnectivityState, 5500);
      }
    };

    // Only check connectivity after page is fully loaded
    if (document.readyState === 'complete') {
      deferredConnectivityCheck();
    } else {
      window.addEventListener('load', deferredConnectivityCheck, { once: true });
    }

    // Set up periodic connectivity checks (every 30 seconds)
    const connectivityInterval = setInterval(updateConnectivityState, 30000);

    const handleOnline = () => {
      console.log('Browser detected online status');
      updateConnectivityState();
    };

    const handleOffline = () => {
      console.log('Browser detected offline status');
      setConnectivity(prev => ({
        ...prev,
        isOnline: false,
        connectionType: 'none',
      }));
    };

    // Listen to browser events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen to visibility changes to recheck connectivity
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateConnectivityState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(connectivityInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConnectivityContext.Provider value={connectivity}>
      {children}
    </ConnectivityContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { useConnectivity } from './use-connectivity';
